let swatchWidth = 32;

function getMaxSwatches(containerWidth) {
  const maxSwatches = Math.floor((containerWidth - 60) / swatchWidth);
  const maxWidth = (maxSwatches * swatchWidth) - 8;

  return { maxSwatches, maxWidth };
}

function initializeDisabledSwatches(product) {

  const containerWidth = product.querySelector('.findify-color-swatches-outer').getBoundingClientRect().width;

  if(containerWidth < 1) {
    return false;
  }

  const { maxSwatches, maxWidth } = getMaxSwatches(containerWidth);

  const swatchesCount = parseInt(product.querySelector('.findify-color-swatches-inner').getAttribute('data-swatches-count'));
  const exceedsMax = swatchesCount > maxSwatches;
  const swiperDOM = product.querySelector('.swiper-wrapper');
  const moreDOM = product.querySelector('.findify-more-swatches');
  swiperDOM.style.maxWidth = maxWidth + 'px';

  const children = Array.from(swiperDOM.children);
  children.slice(0, maxSwatches).forEach(child => child.style.display = null);
  children.slice(maxSwatches).forEach(child => child.style.display = "none");

  if(exceedsMax) {
    const moreCount = swatchesCount - maxSwatches;
    moreDOM.innerText = `+ ${moreCount} ${moreCount === 1 ? 'color' : 'colors'}`;
    moreDOM.style.display = 'block';

  } else {
    moreDOM.style.display = 'none';
  }

  return maxSwatches;
}

/**
 * Keeps a horizontal variant-label list inside its container: shows as many labels
 * as fit on one line and collapses the rest into a single "+N" label.
 *
 * Implemented as a custom element because Findify injects product cards as HTML and
 * evaluates their inline scripts detached from the document (no `document.currentScript`),
 * while custom elements upgrade on their own whenever the markup lands in the DOM.
 *
 * Label widths are estimated with canvas text measurement rather than read back from the
 * DOM. A search or collection page holds dozens of cards, and measuring for real means
 * revealing every label, forcing a layout and reading it back once per card — far too
 * much work for a decoration. The estimate can be a pixel or two out, but the list clips
 * its content, so the worst case is one label more or fewer, never a card that overflows.
 *
 * Findify can also replace a card's pills in place — re-rendering just the label list's
 * children once its own data finishes loading, without removing or re-adding the wrapper
 * element itself. connectedCallback alone would miss that (the wrapper never
 * disconnects), so each instance also watches its own direct children with a
 * MutationObserver, scoped to just this element (no subtree), so it only fires when
 * Findify swaps the pills themselves, never as a side effect of our own writes below.
 */
const variantLabelLists = new Set();
const pendingVariantLabelLists = new Set();
const variantLabelCanvas = document.createElement('canvas').getContext('2d');
let variantLabelFrame = null;

// Bias the fit towards one label fewer, so an underestimate cannot clip a label in half.
const VARIANT_LABEL_SAFETY_MARGIN = 4;

// The container may still be display:none (e.g. a drawer or overlay mid-appear).
// ResizeObserver doesn't reliably report that transition, so poll a few frames.
const VARIANT_LABEL_ZERO_WIDTH_RETRIES = 10;
const variantLabelZeroWidthRetries = new WeakMap();

function scheduleVariantLabelTruncation(list) {
  pendingVariantLabelLists.add(list);

  if (variantLabelFrame === null) {
    variantLabelFrame = requestAnimationFrame(flushVariantLabelTruncation);
  }
}

function retryVariantLabelTruncation(list) {
  const retriesLeft = (variantLabelZeroWidthRetries.get(list) ?? VARIANT_LABEL_ZERO_WIDTH_RETRIES) - 1;

  if (retriesLeft <= 0) {
    variantLabelZeroWidthRetries.delete(list);
    return;
  }

  variantLabelZeroWidthRetries.set(list, retriesLeft);
  requestAnimationFrame(() => {
    // Something else may have truncated it in the meantime (a resize, a later mutation).
    if (!list.hasAttribute('data-truncated')) {
      scheduleVariantLabelTruncation(list);
    }
  });
}

const variantLabelResizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => pendingVariantLabelLists.add(entry.target));
  // Observer callbacks run after layout but before paint, so truncating here — rather
  // than in a frame callback — both keeps the untruncated list from being painted once
  // and keeps the width reads below free of a forced layout.
  flushVariantLabelTruncation();
});

const variantLabelMutationObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => scheduleVariantLabelTruncation(mutation.target));
  flushVariantLabelTruncation();
});

/**
 * Every list on a page shares one set of label styles, so these are read once per pass
 * rather than once per card. Reading them per pass (not once per page) picks up the
 * font-size and gap changes at the mobile breakpoints.
 */
function readVariantLabelMetrics(list, label) {
  const labelStyle = getComputedStyle(label);
  const listStyle = getComputedStyle(list);

  variantLabelCanvas.font = `${labelStyle.fontStyle} ${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;

  return {
    gap: parseFloat(listStyle.columnGap) || 0,
    padding: (parseFloat(labelStyle.paddingLeft) || 0) + (parseFloat(labelStyle.paddingRight) || 0),
    letterSpacing: parseFloat(labelStyle.letterSpacing) || 0
  };
}

function estimateVariantLabelWidth(text, metrics) {
  return variantLabelCanvas.measureText(text).width
    + metrics.padding
    + metrics.letterSpacing * text.length;
}

/**
 * Every write here is guarded to only touch the DOM when the value actually changes.
 * Truncation can run again for a list that's already correctly fitted (e.g. a
 * ResizeObserver firing for an unrelated size change), and an unconditional write —
 * even one that reassigns an identical value — still counts as a real DOM mutation.
 * Writing only on real change makes repeated truncation of a stable list idempotent.
 */
function showFirstVariantLabels(labels, moreLabel, count) {
  labels.forEach((label, index) => {
    const display = index < count ? '' : 'none';
    if (label.style.display !== display) {
      label.style.display = display;
    }
  });

  const remainingCount = labels.length - count;
  const text = remainingCount > 0 ? `+${remainingCount}` : '';
  if (moreLabel.textContent !== text) {
    moreLabel.textContent = text;
  }

  const hidden = remainingCount === 0;
  if (moreLabel.hidden !== hidden) {
    moreLabel.hidden = hidden;
  }
}

/**
 * Truncates every list scheduled since the last frame in one go: all container widths are
 * read first and every write follows, so a page full of cards never becomes a
 * layout-per-card.
 */
function flushVariantLabelTruncation() {
  if (variantLabelFrame !== null) {
    cancelAnimationFrame(variantLabelFrame);
    variantLabelFrame = null;
  }

  const lists = Array.from(pendingVariantLabelLists);
  pendingVariantLabelLists.clear();

  const jobs = [];

  // READ: container widths only. Nothing has been written yet, so this costs no layout.
  lists.forEach((list) => {
    const labels = Array.from(list.querySelectorAll('.label--variant:not(.label--more)'));
    const moreLabel = list.querySelector('.label--more');

    if (!labels.length || !moreLabel) {
      return;
    }

    jobs.push({
      list: list,
      labels: labels,
      moreLabel: moreLabel,
      // clientWidth is the content box of the (clipping) list, so it never reports overflow.
      availableWidth: list.clientWidth
    });
  });

  if (!jobs.length) {
    return;
  }

  const metrics = readVariantLabelMetrics(jobs[0].list, jobs[0].labels[0]);

  // WRITE: from here on it is arithmetic plus one pass of style changes.
  jobs.forEach((job) => {
    const labels = job.labels;
    const total = labels.length;

    labels[0].style.flexShrink = '';
    labels[0].style.minWidth = '';

    // The card has no layout yet (a closed drawer, say). Leave the list in its
    // scrollable state and poll a few frames rather than waiting on a resize that,
    // for a display:none-to-visible transition, isn't guaranteed to come.
    if (job.availableWidth < 1) {
      job.list.removeAttribute('data-truncated');
      showFirstVariantLabels(labels, job.moreLabel, total);
      retryVariantLabelTruncation(job.list);
      return;
    }

    // Reserve room using the widest possible remaining count (only ever narrower afterwards).
    const moreWidth = estimateVariantLabelWidth(`+${total - 1}`, metrics);
    const limit = job.availableWidth - VARIANT_LABEL_SAFETY_MARGIN;

    let usedWidth = 0;
    let visibleCount = 0;
    let firstLabelWidth = 0;

    for (let i = 0; i < total; i++) {
      const labelWidth = estimateVariantLabelWidth(labels[i].textContent.trim(), metrics);
      const widthWithLabel = usedWidth + (visibleCount > 0 ? metrics.gap : 0) + labelWidth;
      const reserve = i < total - 1 ? metrics.gap + moreWidth : 0;

      // Always keep the first label, even when it alone overflows (the list clips it).
      if (widthWithLabel + reserve > limit && visibleCount > 0) {
        break;
      }

      if (i === 0) {
        firstLabelWidth = labelWidth;
      }

      usedWidth = widthWithLabel;
      visibleCount++;
    }

    showFirstVariantLabels(labels, job.moreLabel, visibleCount);
    // Hands the list over to the fitted styles: clipped rather than scrollable.
    job.list.setAttribute('data-truncated', '');
    variantLabelZeroWidthRetries.delete(job.list);

    // In a very narrow card even one label can be too wide: let it ellipsize so the
    // "+N" label stays readable instead of being cut off.
    if (visibleCount === 1 && firstLabelWidth + metrics.gap + moreWidth > limit) {
      labels[0].style.flexShrink = '1';
      labels[0].style.minWidth = '0';
    }
  });
}

class VariantLabelList extends HTMLElement {
  connectedCallback() {
    variantLabelLists.add(this);
    // Observing delivers an initial callback, which schedules the first run. That run
    // happens before the browser has painted the untruncated list.
    variantLabelResizeObserver.observe(this);
    variantLabelMutationObserver.observe(this, { childList: true });
  }

  disconnectedCallback() {
    variantLabelLists.delete(this);
    pendingVariantLabelLists.delete(this);
    variantLabelZeroWidthRetries.delete(this);
    variantLabelResizeObserver.unobserve(this);
    // MutationObserver has no per-target unobserve; a disconnected element with no
    // other references is simply garbage collected along with its observation.
  }
}

if (!customElements.get('variant-label-list')) {
  customElements.define('variant-label-list', VariantLabelList);
}

// Text is measured against the real font, so the estimates shift when the web font swaps in.
if (document.fonts) {
  document.fonts.ready.then(() => {
    variantLabelLists.forEach((list) => scheduleVariantLabelTruncation(list));
  });
}
